import { useEffect, useRef, useState } from "react";

const ADSENSE_CLIENT = "ca-pub-5979838073194083";
const ADSENSE_SCRIPT_SRC = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${ADSENSE_CLIENT}`;
const DEFAULT_AD_SLOT = "8973786709";

function ensureAdSenseScript() {
  if (typeof document === "undefined") return Promise.resolve();

  const existingScript = document.querySelector<HTMLScriptElement>(`script[src="${ADSENSE_SCRIPT_SRC}"]`);
  if (existingScript) return Promise.resolve();

  return new Promise<void>((resolve, reject) => {
    const script = document.createElement("script");
    script.async = true;
    script.src = ADSENSE_SCRIPT_SRC;
    script.crossOrigin = "anonymous";
    script.onload = () => resolve();
    script.onerror = () => reject();
    document.head.appendChild(script);
  });
}

function getAdPlaceholderLabel(variant: AdBannerProps["variant"]) {
  if (variant === "billboard") return "970x250";
  if (variant === "medium-rectangle") return "300x250";
  return "728x90 / 970x90";
}

function requestAdSenseAd() {
  return ensureAdSenseScript().then(() => {
    const adsWindow = window as Window & { adsbygoogle?: unknown[] };
    adsWindow.adsbygoogle = adsWindow.adsbygoogle || [];
    adsWindow.adsbygoogle.push({});
  });
}

interface AdSenseProps {
  adSlot: string;
  format?: "auto" | "horizontal" | "vertical" | "rectangle";
  className?: string;
  style?: React.CSSProperties;
  responsive?: boolean;
}

export function AdSenseBlock({ adSlot, format = "auto", className = "", style = {}, responsive = true }: AdSenseProps) {
  const insRef = useRef<HTMLModElement>(null);

  useEffect(() => {
    if (typeof window === "undefined") return;

    requestAdSenseAd()
      .then(() => {
        // AdSense request queued.
      })
      .catch(() => {
        // Keep the layout stable if the ad script is blocked.
      });
  }, [adSlot]);

  return (
    <ins
      ref={insRef}
      className={`adsbygoogle ${className}`}
      style={{
        display: "block",
        ...style,
      }}
      data-ad-client={ADSENSE_CLIENT}
      data-ad-slot={adSlot}
      data-ad-format={format}
      data-full-width-responsive={responsive ? "true" : "false"}
    />
  );
}

interface AdBannerProps {
  adSlot?: string;
  variant?: "leaderboard" | "billboard" | "medium-rectangle";
  className?: string;
  label?: string;
}

export default function AdBanner({ adSlot = DEFAULT_AD_SLOT, variant = "leaderboard", className = "", label = "Publicidade" }: AdBannerProps) {
  const sizeClasses = {
    leaderboard: "w-full h-24 md:h-32",
    billboard: "w-full h-48 md:h-64",
    "medium-rectangle": "w-full h-64 md:h-80 max-w-md mx-auto",
  };

  const wrapperRef = useRef<HTMLDivElement>(null);
  const adRef = useRef<HTMLModElement>(null);
  const [showFallback, setShowFallback] = useState(Boolean(adSlot));

  useEffect(() => {
    if (!adSlot || typeof window === "undefined") return;

    let cleanup = false;
    let observer: MutationObserver | null = null;

    const updateFallback = () => {
      if (cleanup) return;
      const adElement = adRef.current;
      const status = adElement?.getAttribute("data-ad-status");
      const hasIframe = Boolean(adElement?.querySelector("iframe"));
      setShowFallback(!hasIframe && status !== "filled");
    };

    setShowFallback(true);

    observer = new MutationObserver(updateFallback);
    if (adRef.current) {
      observer.observe(adRef.current, { attributes: true, childList: true, subtree: true });
    }

    requestAdSenseAd()
      .then(() => {
        if (cleanup) return;

        window.setTimeout(updateFallback, 1200);
        window.setTimeout(updateFallback, 3500);
      })
      .catch(() => {
        if (!cleanup) setShowFallback(true);
      });

    return () => {
      cleanup = true;
      observer?.disconnect();
    };
  }, [adSlot]);

  return (
    <div className={`w-full ${className}`}>
      <span className="text-[10px] text-alvinegro-muted uppercase tracking-wider font-medium block text-center mb-1">
        {label}
      </span>
      <div
        ref={wrapperRef}
        className={`relative bg-alvinegro-light/60 rounded-xl flex flex-col items-center justify-center overflow-hidden ${sizeClasses[variant]}`}
      >
        {(!adSlot || showFallback) && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-alvinegro-muted/50 pointer-events-none">
            <i className="ri-advertisement-line text-2xl"></i>
            <span className="text-xs font-medium">{getAdPlaceholderLabel(variant)}</span>
          </div>
        )}
        {adSlot ? (
          <ins
            ref={adRef}
            className="adsbygoogle relative z-10 w-full h-full"
            style={{ display: "block" }}
            data-ad-client={ADSENSE_CLIENT}
            data-ad-slot={adSlot}
            data-ad-format="auto"
            data-full-width-responsive="true"
          />
        ) : null}
      </div>
    </div>
  );
}

interface AdSidebarProps {
  adSlot?: string;
  className?: string;
  label?: string;
}

export function AdSidebar({ adSlot = DEFAULT_AD_SLOT, className = "", label = "Publicidade" }: AdSidebarProps) {
  useEffect(() => {
    if (!adSlot || typeof window === "undefined") return;
    requestAdSenseAd().catch(() => {
      // Keep the placeholder visible if the ad script is blocked.
    });
  }, [adSlot]);

  return (
    <div className={`w-full ${className}`}>
      <span className="text-[10px] text-alvinegro-muted uppercase tracking-wider font-medium block text-center mb-1">
        {label}
      </span>
      <div className="relative bg-alvinegro-light/60 rounded-xl flex flex-col items-center justify-center overflow-hidden w-full h-80 md:h-[500px] max-w-xs mx-auto">
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-alvinegro-muted/50 pointer-events-none">
          <i className="ri-advertisement-line text-2xl"></i>
          <span className="text-xs font-medium">300x250 / 300x600</span>
        </div>
        {adSlot ? (
          <ins
            className="adsbygoogle relative z-10 w-full h-full"
            style={{ display: "block" }}
            data-ad-client={ADSENSE_CLIENT}
            data-ad-slot={adSlot}
            data-ad-format="auto"
            data-full-width-responsive="true"
          />
        ) : (
          <div className="flex flex-col items-center gap-2 text-alvinegro-muted/50">
            <i className="ri-advertisement-line text-2xl"></i>
            <span className="text-xs font-medium">300x250 / 300x600</span>
          </div>
        )}
      </div>
    </div>
  );
}

interface AdInFeedProps {
  adSlot?: string;
  className?: string;
}

export function AdInFeed({ adSlot = DEFAULT_AD_SLOT, className = "" }: AdInFeedProps) {
  useEffect(() => {
    if (!adSlot || typeof window === "undefined") return;
    requestAdSenseAd().catch(() => {
      // Keep the placeholder visible if the ad script is blocked.
    });
  }, [adSlot]);

  return (
    <div className={`w-full ${className}`}>
      <span className="text-[10px] text-alvinegro-muted uppercase tracking-wider font-medium block text-center mb-1">
        Publicidade
      </span>
      <div className="relative bg-alvinegro-light/60 rounded-xl flex flex-col items-center justify-center overflow-hidden w-full h-48 md:h-64">
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-alvinegro-muted/50 pointer-events-none">
          <i className="ri-advertisement-line text-2xl"></i>
          <span className="text-xs font-medium">In-Feed / Fluid</span>
        </div>
        {adSlot ? (
          <ins
            className="adsbygoogle relative z-10 w-full h-full"
            style={{ display: "block" }}
            data-ad-client={ADSENSE_CLIENT}
            data-ad-slot={adSlot}
            data-ad-format="fluid"
            data-ad-layout-key="-fb+5w+4e-db+86"
          />
        ) : (
          <div className="flex flex-col items-center gap-2 text-alvinegro-muted/50">
            <i className="ri-advertisement-line text-2xl"></i>
            <span className="text-xs font-medium">In-Feed / Fluid</span>
          </div>
        )}
      </div>
    </div>
  );
}

interface AdStickyProps {
  adSlot?: string;
  position?: "bottom" | "top";
  className?: string;
}

export function AdSticky({ adSlot = DEFAULT_AD_SLOT, position = "bottom", className = "" }: AdStickyProps) {
  useEffect(() => {
    if (!adSlot || typeof window === "undefined") return;
    requestAdSenseAd().catch(() => {
      // Keep the placeholder visible if the ad script is blocked.
    });
  }, [adSlot]);

  return (
    <div
      className={`fixed left-0 right-0 z-40 bg-white/95 backdrop-blur-sm border-${position === "bottom" ? "t" : "b"} border-alvinegro-border shadow-lg ${className}`}
      style={{ [position]: 0 }}
    >
      <div className="w-full px-4 py-2 flex items-center justify-center">
        <div className="relative w-full max-w-4xl">
          <button
            onClick={(e) => {
              const el = (e.currentTarget as HTMLElement).parentElement?.parentElement?.parentElement;
              if (el) el.style.display = "none";
            }}
            className="absolute -right-1 -top-1 w-6 h-6 bg-alvinegro-black text-white rounded-full flex items-center justify-center text-xs cursor-pointer z-10 hover:bg-alvinegro-dark transition-colors"
            aria-label="Fechar anúncio"
          >
            <i className="ri-close-line"></i>
          </button>
          <div className="w-full h-16 md:h-20 flex items-center justify-center">
            {adSlot ? (
              <ins
                className="adsbygoogle w-full h-full"
                style={{ display: "block" }}
                data-ad-client={ADSENSE_CLIENT}
                data-ad-slot={adSlot}
                data-ad-format="auto"
                data-full-width-responsive="true"
              />
            ) : (
              <div className="flex items-center gap-2 text-alvinegro-muted/50">
                <i className="ri-advertisement-line text-lg"></i>
                <span className="text-xs font-medium">320x50 / 728x90</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

interface AdAnchorProps {
  adSlot?: string;
  className?: string;
}

export function AdAnchor({ adSlot = DEFAULT_AD_SLOT, className = "" }: AdAnchorProps) {
  useEffect(() => {
    if (!adSlot || typeof window === "undefined") return;
    requestAdSenseAd().catch(() => {
      // Keep the placeholder visible if the ad script is blocked.
    });
  }, [adSlot]);

  return (
    <div className={`fixed bottom-0 left-0 right-0 z-50 ${className}`}>
      {adSlot ? (
        <ins
          className="adsbygoogle w-full"
          style={{ display: "block" }}
          data-ad-client={ADSENSE_CLIENT}
          data-ad-slot={adSlot}
          data-ad-format="auto"
          data-full-width-responsive="true"
        />
      ) : (
        <div className="w-full h-16 bg-alvinegro-light/80 backdrop-blur-sm flex items-center justify-center">
          <span className="text-xs text-alvinegro-muted/50">320x50 / 728x90</span>
        </div>
      )}
    </div>
  );
}
